// Utility functions for form data handling and manipulation

/**
 * Safely parse rawFormData JSON strings or objects
 */
export function parseRawFormData(raw: any): any {
  if (!raw) return {};
  try {
    if (typeof raw === 'string') return JSON.parse(raw);
    if (typeof raw === 'object') return raw;
  } catch (_) {
    // ignore parsing errors
  }
  return {};
}

/**
 * Handle nested object updates with dot notation paths
 */
export function handleNestedUpdate(obj: any, path: string | ((prev: any) => any), value?: any): any {
  // Handle function updates
  if (typeof path === 'function') {
    return path(obj);
  }

  // Handle direct path updates
  if (!path || typeof path !== 'string') {
    console.error('Invalid path provided to handleNestedUpdate:', path);
    return obj;
  }

  const pathArray = path.split('.');
  const result = { ...obj };
  let current = result;

  for (let i = 0; i < pathArray.length - 1; i++) {
    const key = pathArray[i];
    if (!(key in current)) {
      current[key] = isNaN(Number(pathArray[i + 1])) ? {} : [];
    }
    current = current[key];
  }

  current[pathArray[pathArray.length - 1]] = value;
  return result;
}

/**
 * Convert a value to a number, handling currency and other formatting
 */
export function toNumber(value: any): number {
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Universal form data change handler that supports both functional updates and path-based updates
 */
export function createFormDataChangeHandler(setFormData: (updater: any) => void) {
  return (pathOrUpdater: string | ((prev: any) => any), value?: any) => {
    // Functional updater passthrough
    if (typeof pathOrUpdater === 'function') {
      setFormData(pathOrUpdater);
      return;
    }
    
    // Ignore invalid types
    if (typeof pathOrUpdater !== 'string') {
      console.warn('handleFormDataChange received unsupported argument', pathOrUpdater);
      return;
    }
    
    setFormData((prev: any) => handleNestedUpdate(prev, pathOrUpdater, value));
  };
}